import Vue from 'vue'
import Vuex from 'vuex'
import pkg from '../package.json'

const { title } = pkg.config

Vue.use(Vuex)

const store = new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  state: {
    error: null,
    loading: true,
    env: process.env.VUE_APP_ENV,
    browser: '',
    touch: false,
    mobile: false,
    phone: false,
    route: '',
    slug: '',
    title: title,
    posts: [],

    navOpen: false,
    mediaOpen: false,
    sidebarOpen: true,
    categoriesOpen: true,
    liveEventsOpen: false,

    ytIsPlaying: false,
    ytIsAudioDescribed: false,

    bgVideoIsPlaying: true,
    bgVideoWasPlaying: true
  },

  getters: {
    posts: state => state.posts,
    post: state => state.posts.find(({ slug }) => slug === state.slug),
    options: state => state.posts.find(({ type }) => type === 'page'),
    guidedtour: state => state.posts.find(({ type }) => type === 'tour'),
    categories: state => state.posts.filter(({ type }) => type === 'category'),
    locations: state => state.posts.filter(({ type }) => type === 'location'),
    subjects: state => state.posts.find(({ slug }) => slug === 'subjects'),
    webinars: state => state.posts.filter(({ type }) => type === 'webinar'),

    getPostBySlug: state => slug => state.posts.find(item => item.slug === slug)

    // getCampus: (state, getters) => slug => {
    //   const category = state.posts.find(item => item.slug === slug)
    //
    //   const categoryLocations = category.children
    //     .filter(({ type }) => type === 'location')
    //     .map(({ id }) => id)
    //
    //   const campusLocations = getters.campus.children.filter(item =>
    //     categoryLocations.includes(item.id)
    //   )
    //
    //   const landmarkLocations = getters.campus.children.filter(
    //     ({ location_type }) => location_type === 'landmark_location'
    //   )
    //
    //   const campus = Object.assign({}, getters.campus, {
    //     children: [...campusLocations, ...landmarkLocations]
    //   })
    //
    //   return campus
    // }
  },

  mutations: {
    browser(state, browser) {
      state.browser = browser
    },
    isLoading(state, loading) {
      state.loading = loading
    },
    isTouch(state) {
      state.touch = true
    },
    isMobile(state, mobile) {
      state.mobile = mobile
    },
    isPhone(state, phone) {
      state.phone = phone
    },
    route(state, route) {
      const slug = route.path.split('/').pop() || '/'
      state.route = route
      state.slug = slug
      const isMedia = route.meta.media
      state.mediaOpen = isMedia || false
    },
    toggleNavState(state) {
      state.navOpen = !state.navOpen
    },
    toggleLiveEventsState(state) {
      state.liveEventsOpen = !state.liveEventsOpen
    },
    toggleCategoriesState(state, isOpen) {
      state.categoriesOpen = isOpen
    },
    toggleSidebarState(state, isOpen) {
      state.sidebarOpen = isOpen
    },
    toggleAudioDescription(state) {
      state.ytIsAudioDescribed = !state.ytIsAudioDescribed
    },
    toggleYouTubeState(state, playing) {
      state.ytIsPlaying = playing
    },
    toggleBgVideoState(state, playing) {
      state.bgVideoIsPlaying = playing
    },
    toggleBgVideoPrevState(state, wasPlaying) {
      state.bgVideoWasPlaying = wasPlaying
    },
    populate(state, { posts, error } = {}) {
      state.error = error

      const data = posts.map(item => {
        const acf = item.acf

        return {
          title: item.title.rendered,
          type: item.type === 'post' ? 'article' : item.type,
          slug: ['page', 'category', 'subject'].includes(item.type)
            ? item.slug
            : `${item.slug}_${item.type}`,
          id: item.id,
          ...acf
        }
      })

      const panoramas = data
        .filter(({ type }) => type === 'panorama' || type === 'campus')
        .map(item => {
          const children = item.children || []

          const hotSpots = children.map(({ entry }) =>
            data.find(o => o.id === entry.ID)
          )

          return {
            ...item,
            children: hotSpots.map((o, i) => ({
              ...o,
              ...item.children[i].position
            }))
          }
        })

      // Essentially 360 groups for guided tours and locations
      const toursOrLocations = data
        .filter(({ type }) => type === 'tour' || type === 'location')
        .map(item => {
          const children = item.children || []
          if (item.type === 'tour') {
            return {
              ...item,
              children: children
                .map(o => panoramas.find(f => f.id === o))
                .filter(o => o)
            }
          }
          return {
            ...item,
            children: children
              .map(o => panoramas.find(f => f.id === o))
              .filter(o => o)
          }
        })

      // Categories and subjects are basically the same
      // They contain content and locations for each entry
      const categoriesAndSubjects = data
        .filter(({ type }) => type === 'category' || type === 'subject')
        .map(item => {
          const children = item.children || []
          const video = item.video ? data.find(o => o.id === item.video) : false

          return {
            ...item,
            video: video,
            children: children
              .map(o => data.find(f => f.id === o))
              .filter(o => o)
              .map(x => {
                const innerChildren = x.children || []
                return {
                  ...x,
                  children: innerChildren
                    .map(o => data.find(f => f.id === o))
                    .filter(o => o)
                }
              })
              .filter(item => {
                if (item.type === 'location' && item.children.length) {
                  return item
                }
                return item
              })
          }
        })

      const subjects = categoriesAndSubjects.filter(
        ({ type }) => type === 'subject'
      )

      const categories = categoriesAndSubjects
        .filter(({ type }) => type === 'category')
        .map(item => {
          if (item.slug === 'subjects') {
            return {
              ...item,
              children: subjects
                .filter(({ type }) => type === 'subject')
                .sort((a, b) => a.title.localeCompare(b.title))
            }
          }
          return {
            ...item
          }
        })

      categories.unshift({
        title: 'Explore all',
        slug: 'explore-all',
        children: toursOrLocations.filter(({ type }) => type === 'location'),
        icon: 'exploreall',
        type: 'category'
      })

      // Really just one page is used for site options ctas etc
      const pages = data
        .filter(({ type }) => type === 'page')
        .map(item => {
          return {
            ...item,
            video: data.find(({ id }) => id === item.video)
          }
        })

      const content = data.filter(
        ({ type }) =>
          type === 'article' ||
          type === 'video' ||
          type === 'gallery' ||
          type === 'audio' ||
          type === 'webinar'
      )

      state.posts = [
        ...panoramas,
        ...toursOrLocations,
        ...categories,
        ...subjects,
        ...pages,
        ...content
      ]
    }
  },

  actions: {}
})

export default store
